// =============================================================================
// Create Terminal Dialog - 创建终端对话框
// =============================================================================

import React, { useState } from 'react';
import { X, Terminal, Settings } from 'lucide-react';
import TerminalTypeSelector from './TerminalTypeSelector';
import ScriptSelector from './ScriptSelector';

export default function CreateTerminalDialog({ 
  isOpen, 
  onClose, 
  onCreateTerminal,
  workspaceId 
}) {
  const [selectedType, setSelectedType] = useState('general');
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptParameters, setScriptParameters] = useState({});
  const [terminalName, setTerminalName] = useState('');
  const [shell, setShell] = useState('/bin/bash');
  const [cols, setCols] = useState(80);
  const [rows, setRows] = useState(24);
  const [isCreating, setIsCreating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const terminalConfig = {
        workspaceId,
        terminalType: selectedType,
        name: terminalName || undefined,
        shell,
        cols,
        rows,
        autoExecuteScript: selectedScript?.id,
        scriptParameters: selectedScript ? scriptParameters : undefined
      };

      await onCreateTerminal(terminalConfig);
      
      // 重置表单
      setSelectedType('general');
      setSelectedScript(null);
      setScriptParameters({});
      setTerminalName('');
      setShell('/bin/bash');
      setCols(80);
      setRows(24);
      setShowAdvanced(false);
      
      onClose();
    } catch (error) {
      console.error('Failed to create terminal:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Terminal className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">创建新终端</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isCreating}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 内容 */}
        <div className="p-6 space-y-6">
          {/* 终端类型选择 */}
          <TerminalTypeSelector
            selectedType={selectedType}
            onTypeSelect={setSelectedType}
            disabled={isCreating}
            showDescription={true}
          />

          {/* 脚本选择 */}
          <ScriptSelector
            selectedScript={selectedScript}
            onScriptSelect={setSelectedScript}
            terminalType={selectedType}
            disabled={isCreating}
            showDescription={true}
          />

          {/* 终端名称 */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              终端名称 <span className="text-gray-400">(可选)</span>
            </label>
            <input
              type="text"
              value={terminalName}
              onChange={(e) => setTerminalName(e.target.value)}
              disabled={isCreating}
              placeholder="留空将自动生成名称"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* 高级设置 */}
          <div className="space-y-3">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              disabled={isCreating}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              <Settings className="w-4 h-4" />
              <span>高级设置</span>
              <span className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {showAdvanced && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                {/* Shell 选择 */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Shell</label>
                  <select
                    value={shell}
                    onChange={(e) => setShell(e.target.value)}
                    disabled={isCreating}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    <option value="/bin/bash">Bash</option>
                    <option value="/bin/sh">Shell</option>
                    <option value="/bin/zsh">Zsh</option>
                    <option value="/bin/fish">Fish</option>
                  </select>
                </div>

                {/* 终端尺寸 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">列数</label>
                    <input
                      type="number"
                      value={cols}
                      onChange={(e) => setCols(parseInt(e.target.value) || 80)}
                      disabled={isCreating}
                      min="20"
                      max="200"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">行数</label>
                    <input
                      type="number"
                      value={rows}
                      onChange={(e) => setRows(parseInt(e.target.value) || 24)}
                      disabled={isCreating}
                      min="10"
                      max="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            disabled={isCreating}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            取消
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isCreating && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>{isCreating ? '创建中...' : '创建终端'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
